import GuestMeetPage from "@/pages/GuestMeetPage";
import WatchMeetPage from "@/pages/WatchMeetPage";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { WebAdvisorShell } from "@/components/webadvisor/WebAdvisorShell";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import PricingPage from "@/pages/PricingPage";
import DemoPage from "@/pages/DemoPage";
import QuotePage from "@/pages/QuotePage";
import SeoLandingPage from "@/pages/SeoLandingPage";
import SeoHubPage from "@/pages/SeoHubPage";
import { BlogListPage, BlogPostPage } from "@/pages/BlogPages";
import BookPage from "@/pages/BookPage";
import ProspectManagerPage from "@/pages/ProspectManagerPage";
import GrowthEstimatorPage from "@/pages/GrowthEstimatorPage";
import CampaignsPage from "@/pages/CampaignsPage";
import MarketingPage from "@/pages/MarketingPage";
import TrialPage from "@/pages/TrialPage";
import PayZellePage from "@/pages/PayZellePage";
import CallCenterPage from "@/pages/CallCenterPage";
import ProspectDialerPage from "@/pages/ProspectDialerPage";
import CallScriptsPage from "@/pages/CallScriptsPage";
import CallCenterManage from "@/pages/CallCenterManage";
import CallCenterPopout from "@/pages/CallCenterPopout";
import GiftCardViewPage, { GiftCardsPage } from "@/pages/GiftCardPages";
import ProfilePage from "@/pages/ProfilePage";
import RepQuotePage from "@/pages/RepQuotePage";
import AgentJoinPage, { ReferralRedirect } from "@/pages/AgentJoinPage";
import AgentPortal from "@/pages/AgentPortal";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ConferencePage from "@/pages/ConferencePage";
import AuthCallback from "@/pages/AuthCallback";
import SalesRepDashboard from "@/pages/SalesRepDashboard";
import WebAdvisorPage from "@/pages/WebAdvisorPage";
import WebAdvisorLeadsPage from "@/pages/WebAdvisorLeadsPage";
import CustomerProfilePage from "@/pages/CustomerProfilePage";
import PublicProposalPage from "@/pages/PublicProposalPage";
import InvoicePage from "@/pages/InvoicePage";
import { ViewAsBanner } from "@/components/ViewAsBanner";
import HostedLandingPage from "@/pages/HostedLandingPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import DirectoryHome from "@/pages/directory/DirectoryHome";
import DirectoryResults from "@/pages/directory/DirectoryResults";
import ShopProfile from "@/pages/directory/ShopProfile";
import DirectoryFeed from "@/pages/directory/DirectoryFeed";
import DirectoryAdmin from "@/pages/directory/DirectoryAdmin";
import ReviewInvitePage from "@/pages/directory/ReviewInvitePage";
import ReviewCardPage from "@/pages/directory/ReviewCardPage";
import MysteryShoppersPage from "@/pages/MysteryShoppersPage";
import AdsAdminPage from "@/pages/AdsAdminPage";
import PodcastStudioPage from "@/pages/PodcastStudioPage";
import PodcastGuestPage from "@/pages/PodcastGuestPage";
import PodcastEpisodePage from "@/pages/PodcastEpisodePage";
import ClaimLanding from "@/pages/claim/ClaimLanding";
import ClaimFlow from "@/pages/claim/ClaimFlow";
import ClaimCampaignAdmin from "@/pages/claim/ClaimCampaignAdmin";
import ClaimQrPage from "@/pages/claim/ClaimQrPage";
import ClientDashboard from "@/pages/client/ClientDashboard";
import AcademyHome from "@/pages/academy/AcademyHome";
import ManualPage from "@/pages/academy/ManualPage";
import CoursePage from "@/pages/academy/CoursePage";
import PracticePage from "@/pages/academy/PracticePage";
import SalesTrainingAdmin from "@/pages/academy/SalesTrainingAdmin";
import SalesDashboard from "@/pages/sales/SalesDashboard";
import SalesManager from "@/pages/sales/SalesManager";
import { Tour } from "@/components/academy/Tour";
import CareersPage from "@/pages/careers/CareersPage";
import TypingPage from "@/pages/academy/TypingPage";
import DemoSchedulePage from "@/pages/DemoSchedulePage";
import SeoDashboard from "@/pages/seo/SeoDashboard";
import GoogleCallback from "@/pages/seo/GoogleCallback";
import { SeoTags } from "@/components/seo/SeoTags";
import JobPage from "@/pages/careers/JobPage";
import ApplicantPortal from "@/pages/careers/ApplicantPortal";
import HiringAdmin from "@/pages/careers/HiringAdmin";
import TrainingShotsAdmin from "@/pages/academy/TrainingShotsAdmin";
import QuizAdmin from "@/pages/academy/QuizAdmin";
import { CaptureButton } from "@/components/academy/CaptureButton";
import RepairOrdersPage from "@/pages/shop/RepairOrdersPage";
import TimeClockPage from "@/pages/shop/TimeClockPage";
import PartsPage from "@/pages/shop/PartsPage";
import StipulatorPage from "@/pages/shop/StipulatorPage";
import InventoryPage from "@/pages/shop/InventoryPage";
import NotFoundPage from "@/pages/NotFoundPage";
import OnboardingShowPage from "@/pages/OnboardingShowPage";
import WatchOnboardingPage from "@/pages/WatchOnboardingPage";
import DemoAccessPage from "@/pages/demo/DemoAccessPage";
import DemoLoginPage, { DemoExpiredPage } from "@/pages/demo/DemoLoginPage";
import DemoHome from "@/pages/demo/DemoHome";
import SandboxPage from "@/pages/demo/SandboxPage";
import { DemoCenter } from "@/components/demos/DemoCenter";
import { DemoBanner } from "@/components/demos/DemoBanner";
import { ClaimPopup } from "@/components/ClaimPopup";

export const safeNext = (search = window.location.search) => { const n = new URLSearchParams(search).get("next") || ""; return n.startsWith("/") && !n.startsWith("//") ? n : ""; };

function ProtectedRoute({ children, adminOnly = false, staffOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`} />;
  const home = ({ sales_rep: "/sales", referral_agent: "/agent", member: "/directory", applicant: "/careers/portal", demo: "/demo/app" }[user.role] || "/dashboard");
  if (user.role === "applicant" && !/^\/(careers|profile)/.test(window.location.pathname)) return <Navigate to="/careers/portal" />;
  if (user.role === "referral_agent" && !/^\/(agent|profile)/.test(window.location.pathname)) return <Navigate to="/agent" />;
  if (user.role === "member" && !/^\/(directory|profile|client|claim|business)/.test(window.location.pathname)) return <Navigate to="/directory" />;
  if (user.role === "demo" && !/^\/(demo\/app|shop\/)/.test(window.location.pathname)) return <Navigate to="/demo/app" />;
  if (adminOnly && user.role !== "admin") return <Navigate to={home} />;
  if (staffOnly && !["admin", "sales_rep"].includes(user.role)) return <Navigate to={home} />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  // Handle Google OAuth callback - check hash for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
    <Routes>
      <Route path="/p/:token" element={<PublicProposalPage />} />
      <Route path="/invoice/:token" element={<InvoicePage />} />
      <Route path="/lp/:site/:slug" element={<HostedLandingPage />} />
      <Route path="/set-password" element={<><Navbar /><SetPasswordPage /></>} />
      <Route path="/" element={<><Navbar /><HomePage /><Footer /></>} />
      <Route path="/products" element={<><Navbar /><ProductsPage /><Footer /></>} />
      <Route path="/pricing" element={<><Navbar /><PricingPage /><Footer /></>} />
      <Route path="/gift-cards" element={<><Navbar /><GiftCardsPage /><Footer /></>} />
      <Route path="/gift-card/:code" element={<GiftCardViewPage />} />
      <Route path="/q/:token" element={<RepQuotePage />} />
      <Route path="/r/:code" element={<ReferralRedirect />} />
      <Route path="/agent-join/:token" element={<><Navbar /><AgentJoinPage /></>} />
      <Route path="/agent" element={<ProtectedRoute><Navbar /><AgentPortal /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Navbar /><ProfilePage /></ProtectedRoute>} />
      <Route path="/demo" element={<><Navbar /><DemoPage /><Footer /></>} />
      <Route path="/demo/schedule/:token" element={<><Navbar /><DemoSchedulePage /><Footer /></>} />
      <Route path="/best-shop-management-software" element={<><Navbar /><SeoLandingPage kind="landing" slug="best-shop-management-software" /><Footer /></>} />
      <Route path="/switch-from-shopmonkey" element={<><Navbar /><SeoLandingPage kind="landing" slug="switch-from-shopmonkey" /><Footer /></>} />
      <Route path="/guides" element={<><Navbar /><SeoHubPage kind="guide" /><Footer /></>} />
      <Route path="/guides/:slug" element={<><Navbar /><SeoLandingPage kind="guide" /><Footer /></>} />
      <Route path="/client/seo" element={<ProtectedRoute><Navbar /><SeoDashboard client /></ProtectedRoute>} />
      <Route path="/admin/seo/google-callback" element={<ProtectedRoute><Navbar /><GoogleCallback /></ProtectedRoute>} />
      <Route path="/admin/seo" element={<ProtectedRoute adminOnly><Navbar /><SeoDashboard /></ProtectedRoute>} />
      <Route path="/quote" element={<><Navbar /><QuotePage /><Footer /></>} />
      <Route path="/login" element={user ? <Navigate replace to={safeNext(location.search) || (user.role === "admin" ? "/admin" : ({ sales_rep: "/sales", referral_agent: "/agent", member: "/directory", applicant: "/careers/portal", demo: "/demo/app" }[user.role] || "/dashboard"))} /> : <><Navbar /><LoginPage /></>} />
      <Route path="/demo-access/:token" element={<DemoAccessPage />} />
      <Route path="/demo-login" element={<DemoLoginPage />} />
      <Route path="/demo-expired" element={<DemoExpiredPage />} />
      <Route path="/demo/app" element={<ProtectedRoute><Navbar /><DemoHome /></ProtectedRoute>} />
      <Route path="/demo/app/:kind" element={<ProtectedRoute><Navbar /><SandboxPage /></ProtectedRoute>} />
      <Route path="/watch/:token" element={<><Navbar /><WatchOnboardingPage /></>} />
      <Route path="/onboarding-show" element={<ProtectedRoute staffOnly><Navbar /><OnboardingShowPage /></ProtectedRoute>} />
      <Route path="/onboarding-show/:sid" element={<ProtectedRoute staffOnly><Navbar /><OnboardingShowPage /></ProtectedRoute>} />
      <Route path="/sales/demos" element={<ProtectedRoute staffOnly><Navbar /><div className="pt-20 pb-10 max-w-[1400px] mx-auto px-4"><DemoCenter /></div></ProtectedRoute>} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <><Navbar /><RegisterPage /></>} />
      <Route path="/join-sales" element={<Navigate replace to="/careers" />} />
      <Route path="/careers" element={<><Navbar /><CareersPage /><Footer /></>} />
      <Route path="/careers/portal" element={<ProtectedRoute><Navbar /><ApplicantPortal /></ProtectedRoute>} />
      <Route path="/careers/:slug" element={<><Navbar /><JobPage /><Footer /></>} />
      <Route path="/admin/hiring" element={<ProtectedRoute staffOnly><Navbar /><HiringAdmin /></ProtectedRoute>} />
      <Route path="/solutions" element={<><Navbar /><SeoHubPage kind="landing" /><Footer /></>} />
      <Route path="/solutions/:slug" element={<><Navbar /><SeoLandingPage kind="landing" /><Footer /></>} />
      <Route path="/compare" element={<><Navbar /><SeoHubPage kind="compare" /><Footer /></>} />
      <Route path="/compare/:slug" element={<><Navbar /><SeoLandingPage kind="compare" /><Footer /></>} />
      <Route path="/blog" element={<><Navbar /><BlogListPage /><Footer /></>} />
      <Route path="/blog/:slug" element={<><Navbar /><BlogPostPage /><Footer /></>} />
      <Route path="/book" element={<><Navbar /><BookPage /><Footer /></>} />
      <Route path="/dashboard" element={<ProtectedRoute><Navbar /><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute><Navbar /><SalesRepDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Navbar /><AdminDashboard /></ProtectedRoute>} />
      <Route path="/webadvisor" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><WebAdvisorPage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/webadvisor/leads" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><WebAdvisorLeadsPage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/webadvisor/prospects" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><ProspectManagerPage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/webadvisor/estimator" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><GrowthEstimatorPage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/webadvisor/campaigns" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><CampaignsPage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/webadvisor/marketing" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><MarketingPage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/trial" element={<><Navbar /><TrialPage /><Footer /></>} />
      <Route path="/pay-zelle" element={<><Navbar /><PayZellePage /><Footer /></>} />
      <Route path="/call-center" element={<ProtectedRoute staffOnly><Navbar hideMobile /><WebAdvisorShell full><CallCenterPage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/prospect-dialer" element={<ProtectedRoute staffOnly><Navbar hideMobile /><ProspectDialerPage /></ProtectedRoute>} />
      <Route path="/call-center/scripts" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><CallScriptsPage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/call-center/manage" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><CallCenterManage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/call-center/popout/:panel" element={<ProtectedRoute staffOnly><CallCenterPopout /></ProtectedRoute>} />
      <Route path="/webadvisor/leads/:leadId" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><CustomerProfilePage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/webadvisor/audit/:auditId" element={<ProtectedRoute staffOnly><Navbar /><WebAdvisorShell><WebAdvisorPage /></WebAdvisorShell></ProtectedRoute>} />
      <Route path="/meet/:code" element={<GuestMeetPage />} />
      <Route path="/podcast/record/:token" element={<PodcastGuestPage />} />
      <Route path="/podcast/:id" element={<><Navbar /><PodcastEpisodePage /><Footer /></>} />
      <Route path="/admin/podcast" element={<ProtectedRoute adminOnly><Navbar /><PodcastStudioPage /></ProtectedRoute>} />
      <Route path="/watch/:code" element={<WatchMeetPage />} />
      <Route path="/conference" element={<ProtectedRoute><Navbar /><ConferencePage /></ProtectedRoute>} />
      <Route path="/conference/:roomId" element={<ProtectedRoute><ConferencePage /></ProtectedRoute>} />
      <Route path="/directory" element={<><Navbar /><DirectoryHome /></>} />
      <Route path="/directory/state/:st" element={<><Navbar /><DirectoryResults /></>} />
      <Route path="/directory/search" element={<><Navbar /><DirectoryResults /></>} />
      <Route path="/directory/near" element={<><Navbar /><DirectoryResults /></>} />
      <Route path="/directory/shop/:slug" element={<><Navbar /><ShopProfile /></>} />
      <Route path="/directory/review/:token" element={<><Navbar /><ReviewInvitePage /></>} />
      <Route path="/directory/card/:slug" element={<ReviewCardPage />} />
      <Route path="/admin/mystery-shoppers" element={<ProtectedRoute staffOnly><Navbar /><MysteryShoppersPage /></ProtectedRoute>} />
      <Route path="/admin/ads" element={<ProtectedRoute adminOnly><Navbar /><AdsAdminPage /></ProtectedRoute>} />
      <Route path="/directory/feed" element={<><Navbar /><DirectoryFeed /></>} />
      <Route path="/directory/admin" element={<ProtectedRoute adminOnly><Navbar /><DirectoryAdmin /></ProtectedRoute>} />
      <Route path="/business/:slug" element={<><Navbar /><ShopProfile /></>} />
      <Route path="/claim-your-page" element={<><Navbar /><ClaimLanding /><Footer /></>} />
      <Route path="/claim/:slug" element={<><Navbar /><ClaimFlow /></>} />
      <Route path="/client/dashboard" element={<ProtectedRoute><Navbar /><ClientDashboard /></ProtectedRoute>} />
      <Route path="/admin/claim-campaign" element={<ProtectedRoute adminOnly><Navbar /><ClaimCampaignAdmin /></ProtectedRoute>} />
      <Route path="/admin/claim-qr" element={<ProtectedRoute adminOnly><div className="print:hidden"><Navbar /></div><ClaimQrPage /></ProtectedRoute>} />
      <Route path="/academy" element={<ProtectedRoute staffOnly><Navbar /><AcademyHome /></ProtectedRoute>} />
      <Route path="/academy/manual" element={<ProtectedRoute staffOnly><Navbar /><ManualPage /></ProtectedRoute>} />
      <Route path="/academy/course/:id" element={<ProtectedRoute staffOnly><Navbar /><CoursePage /></ProtectedRoute>} />
      <Route path="/academy/typing" element={<ProtectedRoute staffOnly><Navbar /><TypingPage /></ProtectedRoute>} />
      <Route path="/academy/practice" element={<ProtectedRoute staffOnly><Navbar /><PracticePage /></ProtectedRoute>} />
      <Route path="/admin/sales-training" element={<ProtectedRoute staffOnly><Navbar /><SalesTrainingAdmin /></ProtectedRoute>} />
      <Route path="/admin/training-shots" element={<ProtectedRoute adminOnly><Navbar /><TrainingShotsAdmin /></ProtectedRoute>} />
      <Route path="/admin/academy-quizzes" element={<ProtectedRoute adminOnly><Navbar /><QuizAdmin /></ProtectedRoute>} />
      <Route path="/shop/repair-orders" element={<ProtectedRoute><Navbar /><RepairOrdersPage /></ProtectedRoute>} />
      <Route path="/shop/time-clock" element={<ProtectedRoute><Navbar /><TimeClockPage /></ProtectedRoute>} />
      <Route path="/shop/parts" element={<ProtectedRoute><Navbar /><PartsPage /></ProtectedRoute>} />
      <Route path="/shop/stipulator" element={<ProtectedRoute><Navbar /><StipulatorPage /></ProtectedRoute>} />
      <Route path="/shop/inventory" element={<ProtectedRoute><Navbar /><InventoryPage /></ProtectedRoute>} />
      <Route path="/sales/dashboard" element={<ProtectedRoute staffOnly><Navbar /><SalesDashboard /></ProtectedRoute>} />
      <Route path="/sales/dashboard/:uid" element={<ProtectedRoute staffOnly><Navbar /><SalesDashboard /></ProtectedRoute>} />
      <Route path="/sales/manager" element={<ProtectedRoute staffOnly><Navbar /><SalesManager /></ProtectedRoute>} />
      <Route path="*" element={<><Navbar /><NotFoundPage /><Footer /></>} />
    </Routes>
    <ClaimPopup />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Tour />
        <CaptureButton />
        <DemoBanner />
        <SeoTags />
        <ViewAsBanner />
        <Toaster position="bottom-right" richColors toastOptions={{ duration: 6000 }} />
      </AuthProvider>
    </BrowserRouter>
  );
}
